import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { io, Socket } from "socket.io-client";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { Input } from "../ui/input";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../ui/use-toast";
import * as chatService from "../../services/chatService";
import { translateText } from "../../services/translationService";
import type {
  ChatMessage,
  ChatReadResponse,
  ChatRoom,
  UserSummary,
} from "../../types/api";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { ImagePlus, Send, X } from "lucide-react";

const API_BASE = (
  process.env.REACT_APP_API_URL ?? "http://localhost:5000/api/v1"
).replace(/\/api\/v1$/, "");

type IncomingMessage = {
  roomId: number;
  message: ChatMessage;
  room?: ChatRoom;
};

type IncomingRead = {
  roomId: number;
  readerId: number;
  messages: ChatMessage[];
};

const isManagerRole = (role?: string | null) =>
  role === "manager_contact" || role === "manager_trader" || role === "admin";

type ChatWidgetProps = {
  variant?: "widget" | "panel";
  className?: string;
  showLauncher?: boolean;
  onUnreadChange?: (count: number) => void;
};

/** Jour local (YYYY-MM-DD) pour regrouper les messages façon messagerie */
const messageDayKey = (iso: string | Date) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Dates éloignées : style messagerie type « Tue, 25 Nov at 10:06 AM » (adapté par langue).
 */
function formatDistantChatSeparator(
  messageDate: Date,
  t: (key: string) => string,
  lang: string
): string {
  const now = new Date();
  const locale = lang.startsWith("fr")
    ? "fr-FR"
    : lang.startsWith("de")
    ? "de-DE"
    : lang.startsWith("pt")
    ? "pt-BR"
    : "en-US";

  const weekday = messageDate.toLocaleDateString(locale, { weekday: "short" });
  const dayNum = messageDate.getDate();
  const month = messageDate.toLocaleDateString(locale, { month: "short" });
  const y = messageDate.getFullYear();
  const currentY = now.getFullYear();
  const yearPart = y !== currentY ? ` ${y}` : "";
  const at = t("chat.dateSeparatorAt");

  if (lang.startsWith("en")) {
    const time = messageDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${weekday}, ${dayNum} ${month}${yearPart} ${at} ${time}`;
  }

  if (lang.startsWith("fr")) {
    const time = messageDate.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${weekday} ${dayNum} ${month}${yearPart} ${at} ${time}`;
  }

  if (lang.startsWith("de")) {
    const time = messageDate.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${weekday}, ${dayNum}. ${month}${yearPart} ${at} ${time}`;
  }

  if (lang.startsWith("pt")) {
    const time = messageDate.toLocaleTimeString("pt-BR", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${weekday}, ${dayNum} ${month}${yearPart} ${at} ${time}`;
  }

  const time = messageDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${weekday}, ${dayNum} ${month}${yearPart} ${at} ${time}`;
}

function formatChatDaySeparator(
  messageDate: Date,
  t: (key: string) => string,
  lang: string
): string {
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const d0 = startOfDay(messageDate);
  const dToday = startOfDay(now);
  const diffDays = Math.round((dToday - d0) / 86_400_000);

  const locale = lang.startsWith("fr")
    ? "fr-FR"
    : lang.startsWith("de")
    ? "de-DE"
    : lang.startsWith("pt")
    ? "pt-BR"
    : "en-US";

  const timePart = messageDate.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (diffDays === 0) {
    return `${t("chat.dateSeparatorToday")} · ${timePart}`;
  }
  if (diffDays === 1) {
    return `${t("chat.dateSeparatorYesterday")} · ${timePart}`;
  }

  return formatDistantChatSeparator(messageDate, t, lang);
}

const sortRooms = (list: ChatRoom[]) => {
  const toTimestamp = (room: ChatRoom) =>
    new Date(
      room.lastMessage?.createdAt ?? room.updatedAt ?? room.createdAt
    ).getTime();
  return [...list].sort((a, b) => toTimestamp(b) - toTimestamp(a));
};

const ChatWidget = ({
  variant = "widget",
  className,
  showLauncher = true,
  onUnreadChange,
}: ChatWidgetProps) => {
  const isPanel = variant === "panel";
  const { user, accessToken, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(isPanel);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sendingImage, setSendingImage] = useState(false);
  /** Prévisualisation avant envoi (style WhatsApp) */
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState<
    string | null
  >(null);
  const [previewCaption, setPreviewCaption] = useState("");
  /** Lightbox pour voir une image du fil sans quitter la page */
  /** Aperçu plein écran : URL + légende optionnelle */
  const [lightbox, setLightbox] = useState<{
    url: string;
    caption: string;
  } | null>(null);
  const previewBlobUrlRef = useRef<string | null>(null);
  const [isLoadingRoom, setIsLoadingRoom] = useState(false);
  const [participants, setParticipants] = useState<UserSummary[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<string>("");
  const socketRef = useRef<Socket | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const activeRoomRef = useRef<ChatRoom | null>(null);
  const userIdRef = useRef<number | undefined>(user?.id);
  const markReadLockRef = useRef<Map<number, boolean>>(new Map());
  const { t } = useTranslation();

  const isClient = user?.role === "client";
  const isManager = isManagerRole(user?.role);
  const isAdmin = user?.role === "admin";
  const canUseChat = isAuthenticated && (isClient || isManager);
  const shouldConnect = canUseChat && (isPanel || isOpen);

  useEffect(() => {
    if (isPanel) {
      setIsOpen(true);
    }
  }, [isPanel]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  const updateRooms = useCallback(
    (updater: (prev: ChatRoom[]) => ChatRoom[]) => {
      setRooms((prev) => sortRooms(updater(prev)));
    },
    []
  );

  const markRoomRead = useCallback(
    async (roomId: number): Promise<ChatReadResponse | null> => {
      if (!shouldConnect) {
        return null;
      }
      if (markReadLockRef.current.get(roomId)) {
        return null;
      }
      markReadLockRef.current.set(roomId, true);
      try {
        const result = await chatService.markRoomRead(roomId);
        if (result.messages.length) {
          const timestamps = new Map(
            result.messages.map((message) => [message.id, message.readAt])
          );
          setMessages((prev) =>
            prev.map((message) =>
              timestamps.has(message.id)
                ? {
                    ...message,
                    readAt: timestamps.get(message.id) ?? message.readAt,
                  }
                : message
            )
          );
          updateRooms((prev) =>
            prev.map((room) =>
              room.id === roomId ? { ...room, unreadCount: 0 } : room
            )
          );
        }
        return result;
      } catch (error) {
        return null;
      } finally {
        markReadLockRef.current.delete(roomId);
      }
    },
    [shouldConnect, updateRooms]
  );

  const loadMessages = useCallback(
    async (room: ChatRoom) => {
      setIsLoadingRoom(true);
      try {
        const history = await chatService.listMessages(room.id);
        setMessages(history);
        await markRoomRead(room.id);
      } catch (error) {
        toast({
          title: "Impossible d'ouvrir la discussion",
          description:
            (error as string) ?? "Vérifiez votre connexion puis réessayez.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingRoom(false);
      }
    },
    [markRoomRead, toast]
  );

  const normalizeRooms = useCallback(
    (data: ChatRoom[]) =>
      sortRooms(
        data.map((room) => ({
          ...room,
          unreadCount: room.unreadCount ?? 0,
        }))
      ),
    []
  );

  const refreshRooms = useCallback(async () => {
    if (!canUseChat) return;
    try {
      let fetched = await chatService.listRooms();
      if (isClient && fetched.length === 0) {
        const room = await chatService.ensureRoom();
        fetched = [room];
      }
      const normalized = normalizeRooms(fetched);
      setRooms(normalized);
      if (!normalized.length) {
        setActiveRoom(null);
        setMessages([]);
        return;
      }
      setActiveRoom((prev) => {
        if (!prev) {
          return normalized[0];
        }
        const updated = normalized.find((room) => room.id === prev.id);
        return updated ?? normalized[0];
      });
    } catch (error: any) {
      // Ne pas afficher d'erreur pour les 401 (session expirée)
      if (
        error?.response?.status === 401 ||
        error?.includes?.("Session expirée")
      ) {
        setRooms([]);
        setActiveRoom(null);
        setMessages([]);
        return;
      }
      toast({
        title: t("chat.loadRoomsErrorTitle"),
        description: (error as string) ?? t("chat.loadRoomsErrorDescription"),
        variant: "destructive",
      });
    }
  }, [canUseChat, isClient, normalizeRooms, t, toast]);

  const loadContacts = useCallback(async () => {
    if (!isManager) return;
    try {
      const contacts = await chatService.listContacts();
      setParticipants(contacts);
    } catch (error) {
      toast({
        title: t("chat.loadContactsErrorTitle"),
        description:
          (error as string) ?? t("chat.loadContactsErrorDescription"),
        variant: "destructive",
      });
    }
  }, [isManager, t, toast]);

  const getRoomContact = useCallback(
    (room: ChatRoom) => {
      if (!user) {
        return null;
      }
      if (room.managerId === user.id) {
        return room.client ?? null;
      }
      return room.manager ?? null;
    },
    [user]
  );

  const contactsLabel = t("chat.contactsLabel");

  const connectSocket = useCallback(() => {
    if (!accessToken || socketRef.current) return;
    const socket = io(API_BASE, {
      auth: { token: accessToken },
    });

    socket.on("chat:message", (payload: IncomingMessage) => {
      const currentRoom = activeRoomRef.current;
      const currentUserId = userIdRef.current;
      const isCurrentRoom = currentRoom?.id === payload.roomId;
      const isOwnMessage = payload.message.senderId === currentUserId;

      updateRooms((prev) => {
        const existing = prev.find((room) => room.id === payload.roomId);
        if (!existing && !payload.room) {
          return prev;
        }
        const unreadIncrement =
          !isOwnMessage && (!isCurrentRoom || !shouldConnect) ? 1 : 0;

        if (existing) {
          return prev.map((room) =>
            room.id === payload.roomId
              ? {
                  ...room,
                  lastMessage: payload.message,
                  unreadCount: Math.max(
                    0,
                    (room.unreadCount ?? 0) + unreadIncrement
                  ),
                }
              : room
          );
        }
        return [
          {
            ...payload.room!,
            lastMessage: payload.message,
            unreadCount: unreadIncrement,
          },
          ...prev,
        ];
      });

      if (isCurrentRoom) {
        setMessages((prev) => {
          // Vérifier si le message existe déjà par ID ou par contenu + timestamp pour éviter les doublons
          const existsById = prev.some(
            (message) => message.id === payload.message.id
          );
          if (existsById) {
            return prev;
          }
          // Vérifier aussi par contenu et timestamp pour les cas où l'ID pourrait être différent
          const existsByContent = prev.some(
            (message) =>
              message.content === payload.message.content &&
              message.senderId === payload.message.senderId &&
              message.imageUrl === payload.message.imageUrl &&
              Math.abs(
                new Date(message.createdAt).getTime() -
                  new Date(payload.message.createdAt).getTime()
              ) < 1000 // Moins d'1 seconde de différence
          );
          if (existsByContent) {
            return prev;
          }
          return [...prev, payload.message];
        });
        if (!isOwnMessage) {
          void markRoomRead(payload.roomId);
        }
      }
    });

    socket.on("chat:read", (payload: IncomingRead) => {
      if (!payload.messages.length) {
        return;
      }
      const readMap = new Map(
        payload.messages.map((message) => [message.id, message.readAt])
      );
      setMessages((prev) =>
        prev.map((message) =>
          readMap.has(message.id)
            ? { ...message, readAt: readMap.get(message.id) ?? message.readAt }
            : message
        )
      );
      if (payload.readerId === userIdRef.current) {
        updateRooms((prev) =>
          prev.map((room) =>
            room.id === payload.roomId ? { ...room, unreadCount: 0 } : room
          )
        );
      }
    });

    socketRef.current = socket;
  }, [accessToken, markRoomRead, shouldConnect, updateRooms]);

  useEffect(() => {
    if (shouldConnect) {
      connectSocket();
    } else if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    return () => {
      if (socketRef.current && !shouldConnect) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connectSocket, shouldConnect]);

  useEffect(() => {
    if (!canUseChat) {
      return;
    }
    void refreshRooms();
    if (isManager) {
      void loadContacts();
    }
  }, [canUseChat, isManager, loadContacts, refreshRooms]);

  useEffect(() => {
    if (!shouldConnect || !activeRoom) {
      return;
    }
    void loadMessages(activeRoom);
  }, [shouldConnect, activeRoom, loadMessages]);

  const unreadTotal = useMemo(
    () => rooms.reduce((acc, room) => acc + (room.unreadCount ?? 0), 0),
    [rooms]
  );

  const [messageTranslations, setMessageTranslations] = useState<
    Record<
      number,
      {
        translated: string;
        showing: boolean;
        loading: boolean;
      }
    >
  >({});

  useEffect(() => {
    onUnreadChange?.(unreadTotal);
  }, [onUnreadChange, unreadTotal]);

  const lastReadMessageId = useMemo(() => {
    if (!user?.id) return null;
    const readMessages = messages.filter(
      (message) => message.senderId === user.id && message.readAt
    );
    return readMessages.length
      ? readMessages[readMessages.length - 1].id
      : null;
  }, [messages, user?.id]);

  const sortedChatMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [messages]
  );

  const title = useMemo(() => {
    if (!activeRoom) {
      if (isClient) {
        return t("chat.titleDefaultClient");
      }
      return isAdmin
        ? t("chat.titleDefaultAdmin")
        : t("chat.titleDefaultManager");
    }
    if (isClient) {
      // Afficher "Administrateur" si le manager est un admin, sinon le nom du manager
      if (activeRoom.manager?.role === "admin") {
        return t("chat.titleAdmin");
      }
      const name = `${activeRoom.manager?.firstName ?? ""} ${
        activeRoom.manager?.lastName ?? ""
      }`.trim();
      return name
        ? t("chat.titleManagerPrefix", { name })
        : t("chat.titleDefaultClient");
    }
    const contact = getRoomContact(activeRoom);
    const name = `${contact?.firstName ?? ""} ${
      contact?.lastName ?? ""
    }`.trim();
    return name || t("chat.titleContactFallback");
  }, [activeRoom, getRoomContact, isAdmin, isClient, t]);

  const handleSend = async () => {
    if (!activeRoom || !input.trim() || sendingImage) return;
    const text = input.trim();
    setInput("");
    try {
      // Envoyer le message - le WebSocket l'ajoutera automatiquement à la liste
      await chatService.sendMessage(activeRoom.id, text);
      // Ne pas ajouter le message manuellement ici car le WebSocket le fera
      // Cela évite les doublons visuels
    } catch (error) {
      toast({
        title: t("chat.sendErrorTitle", "Envoi impossible"),
        description:
          (error as string) ??
          t("chat.sendErrorDescription", "Veuillez réessayer."),
        variant: "destructive",
      });
      setInput(text);
    }
  };

  const closeImagePreview = useCallback(() => {
    const u = previewBlobUrlRef.current;
    if (u) {
      URL.revokeObjectURL(u);
      previewBlobUrlRef.current = null;
    }
    setPendingImagePreviewUrl(null);
    setPendingImageFile(null);
    setPreviewCaption("");
  }, []);

  const confirmSendPreviewImage = useCallback(async () => {
    if (!pendingImageFile || !activeRoom || sendingImage) return;
    setSendingImage(true);
    try {
      await chatService.sendChatImage(
        activeRoom.id,
        pendingImageFile,
        previewCaption.trim() || undefined
      );
      closeImagePreview();
    } catch (error) {
      toast({
        title: t("chat.sendImageErrorTitle", "Envoi impossible"),
        description:
          (error as string) ??
          t("chat.sendImageErrorDescription", "Veuillez réessayer."),
        variant: "destructive",
      });
    } finally {
      setSendingImage(false);
    }
  }, [
    activeRoom,
    closeImagePreview,
    pendingImageFile,
    previewCaption,
    sendingImage,
    t,
    toast,
  ]);

  const handleChatImageSelected = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !activeRoom || sendingImage) return;
    const url = URL.createObjectURL(file);
    previewBlobUrlRef.current = url;
    setPendingImageFile(file);
    setPendingImagePreviewUrl(url);
    setPreviewCaption(input.trim());
    setInput("");
  };

  useEffect(() => {
    return () => {
      const u = previewBlobUrlRef.current;
      if (u) URL.revokeObjectURL(u);
    };
  }, []);

  useEffect(() => {
    if (!pendingImagePreviewUrl && !lightbox) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [pendingImagePreviewUrl, lightbox]);

  useEffect(() => {
    if (!pendingImagePreviewUrl && !lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (pendingImagePreviewUrl) closeImagePreview();
      else setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingImagePreviewUrl, lightbox, closeImagePreview]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSelectRoom = async (room: ChatRoom) => {
    setActiveRoom(room);
    await loadMessages(room);
  };

  const handleStartConversation = async () => {
    if (!selectedParticipant) return;
    try {
      const room = await chatService.ensureRoom(Number(selectedParticipant));
      updateRooms((prev) => {
        const exists = prev.some((item) => item.id === room.id);
        const next = exists
          ? prev.map((item) =>
              item.id === room.id ? { ...item, ...room } : item
            )
          : [{ ...room, unreadCount: room.unreadCount ?? 0 }, ...prev];
        return next;
      });
      setSelectedParticipant("");
      await handleSelectRoom(room);
    } catch (error) {
      toast({
        title: t("chat.startErrorTitle"),
        description: (error as string) ?? t("chat.startErrorDescription"),
        variant: "destructive",
      });
    }
  };

  if (!canUseChat) {
    return null;
  }

  const chatOverlays =
    typeof document !== "undefined" &&
    (pendingImagePreviewUrl || lightbox)
      ? createPortal(
          <>
            {pendingImagePreviewUrl && (
              <div
                className="fixed inset-0 z-[100] flex flex-col bg-[#0b141a] text-white"
                role="dialog"
                aria-modal="true"
                aria-labelledby="chat-image-preview-title">
                <header className="flex shrink-0 items-center justify-between border-b border-white/10 px-2 py-2 sm:px-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={closeImagePreview}
                    aria-label={t("chat.imagePreviewCancel")}>
                    <X className="h-6 w-6" />
                  </Button>
                  <span
                    id="chat-image-preview-title"
                    className="text-sm font-medium">
                    {t("chat.imagePreviewTitle")}
                  </span>
                  <div className="w-10 shrink-0" aria-hidden />
                </header>
                <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
                  <img
                    src={pendingImagePreviewUrl}
                    alt=""
                    className="max-h-[min(60vh,560px)] max-w-full object-contain"
                  />
                </div>
                <div className="shrink-0 border-t border-white/10 bg-black/50 p-4">
                  <p className="mb-2 text-center text-xs text-white/55">
                    {t("chat.imagePreviewCaptionHint")}
                  </p>
                  <div className="mx-auto flex max-w-2xl items-end gap-2">
                    <Input
                      className="flex-1 border-white/20 bg-white/10 text-white placeholder:text-white/50"
                      value={previewCaption}
                      onChange={(e) => setPreviewCaption(e.target.value)}
                      placeholder={t("chat.imagePreviewCaptionPlaceholder")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void confirmSendPreviewImage();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="icon"
                      className="h-12 w-12 shrink-0 rounded-full bg-cyan-500 text-black hover:bg-cyan-400"
                      disabled={sendingImage}
                      onClick={() => void confirmSendPreviewImage()}
                      aria-label={t("chat.imagePreviewSend")}>
                      {sendingImage ? (
                        <span className="text-xs font-medium">…</span>
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {lightbox && (
              <div
                className="fixed inset-0 z-[100] flex flex-col bg-black/95"
                role="dialog"
                aria-modal="true"
                aria-label={t("chat.imagePreviewLightboxAria")}>
                <div className="flex shrink-0 justify-end p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={() => setLightbox(null)}
                    aria-label={t("chat.imagePreviewCancel")}>
                    <X className="h-6 w-6" />
                  </Button>
                </div>
                <div
                  className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4"
                  onClick={() => setLightbox(null)}>
                  <div
                    className="flex max-w-full flex-col items-center gap-3"
                    onClick={(e) => e.stopPropagation()}>
                    <img
                      src={lightbox.url}
                      alt={t("chat.imageAlt")}
                      className="max-h-[min(75vh,800px)] max-w-full object-contain"
                    />
                    {lightbox.caption ? (
                      <p className="max-w-[min(90vw,520px)] text-center text-sm leading-relaxed text-white/95">
                        {lightbox.caption}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </>,
          document.body
        )
      : null;

  const renderRoomsList = () => {
    return (
      <div className="flex w-full min-h-0 max-w-xs shrink-0 flex-col border-r border-border pr-4 dark:border-white/10">
        {isManager && (
          <div className="mb-4 space-y-2">
            <h4 className="text-sm font-semibold text-foreground/90 dark:text-white/80">
              {contactsLabel}
            </h4>
            <div className="flex min-w-0 items-center gap-2">
              <Select
                value={selectedParticipant}
                onValueChange={(value) => setSelectedParticipant(value)}>
                <SelectTrigger className="h-10 min-w-0 flex-1">
                  <SelectValue
                    placeholder={
                      isAdmin
                        ? t(
                            "chat.selectContactPlaceholder",
                            "Choisir un contact"
                          )
                        : t(
                            "chat.selectContactPlaceholder",
                            "Choisir un contact"
                          )
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {participants.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground dark:text-white/60">
                      {t("chat.contactsEmpty")}
                    </div>
                  )}
                  {participants.map((participant) => {
                    const name =
                      `${participant.firstName} ${participant.lastName}`.trim();
                    const label =
                      isAdmin && participant.role !== "client"
                        ? `${name} (${participant.role})`
                        : name;
                    return (
                      <SelectItem
                        key={participant.id}
                        value={String(participant.id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!selectedParticipant}
                onClick={() => void handleStartConversation()}>
                {t("chat.startButton")}
              </Button>
            </div>
          </div>
        )}
        <div
          className={cn(
            "flex min-h-0 flex-col gap-2 overflow-y-auto rounded-xl border border-border bg-muted/30 p-2 pr-2 dark:border-white/10 dark:bg-white/5",
            "max-h-[min(420px,50vh)]",
            isManager && "max-h-[min(340px,45vh)]"
          )}>
          {rooms.length === 0 && (
            <p className="text-sm text-muted-foreground dark:text-white/50">
              {isClient
                ? t("chat.roomsClientEmpty")
                : isAdmin
                ? t("chat.roomsAdminEmpty")
                : t("chat.roomsManagerEmpty")}
            </p>
          )}
          {rooms.map((room) => {
            const contact = getRoomContact(room);
            const contactName = `${contact?.firstName ?? ""} ${
              contact?.lastName ?? ""
            }`.trim();
            // Pour les clients, afficher le nom du manager ou de l'admin
            const displayName = isClient
              ? contactName ||
                (room.manager?.role === "admin"
                  ? "Administrateur"
                  : "Mon manager")
              : contactName || "Contact";
            const active = room.id === activeRoom?.id;
            return (
              <button
                key={room.id}
                onClick={() => void handleSelectRoom(room)}
                className={cn(
                  "rounded-xl border px-3 py-2 text-left transition",
                  active
                    ? "border-cyan-500 bg-cyan-500/15 text-foreground dark:border-cyan-400 dark:bg-cyan-400/10 dark:text-white"
                    : "border-border bg-background text-foreground/80 hover:border-cyan-500/40 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30"
                )}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{displayName}</div>
                  {(room.unreadCount ?? 0) > 0 && (
                    <span className="rounded-full bg-cyan-400 px-2 text-xs font-semibold text-black">
                      {room.unreadCount}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2 dark:text-white/60">
                  {room.lastMessage
                    ? (() => {
                        const lm = room.lastMessage;
                        const prefix =
                          lm.senderId === user?.id
                            ? t("chat.roomsLastMessageYouPrefix")
                            : "";
                        if (lm.imageUrl) {
                          const cap = lm.content?.trim();
                          return cap
                            ? `${prefix}${t("chat.roomsLastMessageImage")} · ${cap.slice(0, 50)}${cap.length > 50 ? "…" : ""}`
                            : `${prefix}${t("chat.roomsLastMessageImage")}`;
                        }
                        return `${prefix}${lm.content}`;
                      })()
                    : t("chat.roomsLastMessageEmpty")}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (isLoadingRoom) {
      return (
        <p className="text-center text-sm text-muted-foreground dark:text-white/70">
          {t("chat.messagesLoading")}
        </p>
      );
    }
    if (!activeRoom) {
      return (
        <div className="flex min-h-[12rem] flex-1 items-center justify-center text-muted-foreground dark:text-white/50">
          {isManager
            ? isAdmin
              ? t("chat.messagesEmptyAdmin")
              : t("chat.messagesEmptyManager")
            : t("chat.messagesEmptyClient")}
        </div>
      );
    }
    return (
      <div className="flex h-80 min-h-0 flex-col space-y-3 overflow-y-auto rounded-xl border border-border bg-muted/20 p-2 pr-2 scrollbar-thin dark:border-white/10 dark:bg-white/5 dark:scrollbar-thumb-white/10">
        {sortedChatMessages.map((message, index) => {
          const isMine = message.senderId === user?.id;
          const isLastRead = message.id === lastReadMessageId;
          const translationState = messageTranslations[message.id];
          const isShowingTranslated =
            translationState &&
            !translationState.loading &&
            translationState.showing;
          const prev = index > 0 ? sortedChatMessages[index - 1] : null;
          const showDaySeparator =
            !prev ||
            messageDayKey(prev.createdAt) !== messageDayKey(message.createdAt);
          return (
            <Fragment key={message.id}>
              {showDaySeparator && (
                <div
                  className="flex w-full shrink-0 justify-center py-1"
                  role="separator"
                  aria-hidden>
                  <span
                    className="max-w-[90%] rounded-full bg-muted/90 px-3 py-1 text-center text-[11px] font-medium leading-snug text-muted-foreground shadow-sm dark:bg-white/10 dark:text-white/75">
                    {formatChatDaySeparator(
                      new Date(message.createdAt),
                      (key) => t(key),
                      i18n.language
                    )}
                  </span>
                </div>
              )}
              <div
                className={cn("flex w-full", {
                  "justify-end": isMine,
                  "justify-start": !isMine,
                })}>
                <div
                className={cn(
                  "max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm",
                  isMine
                    ? "bg-cyan-500 text-black"
                    : "bg-muted text-foreground dark:bg-white/10 dark:text-white"
                )}>
                {message.imageUrl ? (
                  <div className="mb-2 overflow-hidden rounded-lg">
                    <button
                      type="button"
                      className="block w-full cursor-zoom-in p-0 text-left outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-cyan-400"
                      onClick={() =>
                        setLightbox({
                          url: message.imageUrl!,
                          caption:
                            isShowingTranslated &&
                            translationState?.translated
                              ? translationState.translated
                              : message.content?.trim() ?? "",
                        })
                      }
                      aria-label={t("chat.imageOpenLightboxAria")}>
                      <img
                        src={message.imageUrl}
                        alt={t("chat.imageAlt")}
                        className={cn(
                          "max-h-56 w-full max-w-full object-contain",
                          message.content.trim()
                            ? "rounded-t-lg"
                            : "rounded-lg"
                        )}
                      />
                    </button>
                    {message.content.trim() ? (
                      <p
                        className={cn(
                          "border-t px-2.5 py-2 text-sm leading-snug",
                          isMine
                            ? "border-black/15 bg-black/10 text-black"
                            : "border-white/10 bg-black/15 text-foreground dark:text-white"
                        )}>
                        {isShowingTranslated
                          ? translationState?.translated
                          : message.content}
                      </p>
                    ) : null}
                  </div>
                ) : message.content.trim() ? (
                  <p>
                    {isShowingTranslated
                      ? translationState?.translated
                      : message.content}
                  </p>
                ) : null}
                {!isMine && message.content.trim() && (
                  <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground dark:text-white/60">
                    <button
                      type="button"
                      className="underline-offset-2 hover:underline"
                      disabled={translationState?.loading}
                      onClick={async () => {
                        setMessageTranslations((prev) => ({
                          ...prev,
                          [message.id]: {
                            translated: prev[message.id]?.translated ?? "",
                            showing: prev[message.id]?.showing ?? false,
                            loading: true,
                          },
                        }));
                        try {
                          let translated = translationState?.translated;
                          if (!translated) {
                            const targetLang: "fr" | "en" | "de" | "pt" =
                              i18n.language.startsWith("fr")
                                ? "fr"
                                : i18n.language.startsWith("de")
                                ? "de"
                                : i18n.language.startsWith("pt")
                                ? "pt"
                                : "en";
                            translated = await translateText({
                              text: message.content,
                              targetLang,
                            });
                          }
                          setMessageTranslations((prev) => ({
                            ...prev,
                            [message.id]: {
                              translated: translated ?? message.content,
                              showing: !translationState?.showing,
                              loading: false,
                            },
                          }));
                        } catch {
                          setMessageTranslations((prev) => ({
                            ...prev,
                            [message.id]: {
                              translated: prev[message.id]?.translated ?? "",
                              showing: false,
                              loading: false,
                            },
                          }));
                          toast({
                            title: t(
                              "chat.translateErrorTitle",
                              "Traduction impossible"
                            ),
                            description: t(
                              "chat.translateErrorDescription",
                              "Veuillez réessayer plus tard."
                            ),
                            variant: "destructive",
                          });
                        }
                      }}>
                      {translationState?.loading
                        ? t("chat.translateLoading", "Traduction…")
                        : isShowingTranslated
                        ? t("chat.translateShowOriginal", "Afficher l'original")
                        : t("chat.translateButton", "Traduire")}
                    </button>
                  </div>
                )}
                <span className="mt-1 block text-right text-[11px] text-muted-foreground dark:text-white/70">
                  {new Date(message.createdAt).toLocaleTimeString(
                    i18n.language.startsWith("fr")
                      ? "fr-FR"
                      : i18n.language.startsWith("de")
                      ? "de-DE"
                      : i18n.language.startsWith("pt")
                      ? "pt-BR"
                      : "en-US",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </span>
                {isMine && isLastRead && (
                  <span className="mt-0.5 block text-right text-[11px] text-muted-foreground dark:text-white/70">
                    {t("chat.seenLabel")}
                  </span>
                )}
                </div>
              </div>
            </Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>
    );
  };

  const renderInput = () => {
    if (!activeRoom) return null;
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleChatImageSelected}
        />
        <Input
          placeholder={t("chat.inputPlaceholder")}
          value={input}
          disabled={sendingImage}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleSend();
            }
          }}
        />
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={sendingImage || !!pendingImagePreviewUrl}
            aria-label={t("chat.attachImageAria")}
            onClick={() => fileInputRef.current?.click()}>
            <ImagePlus className="h-4 w-4" />
          </Button>
          <Button
            className="min-w-[5rem]"
            disabled={!input.trim() || sendingImage}
            onClick={() => void handleSend()}>
            {sendingImage ? t("chat.sendingImage") : t("chat.sendButton")}
          </Button>
        </div>
      </div>
    );
  };

  const conversationLayout = (
    <div className="flex min-h-0 flex-col gap-6 sm:flex-row">
      {renderRoomsList()}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <div className="rounded-2xl border border-border bg-muted/30 p-4 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
          <p className="text-sm text-muted-foreground dark:text-white/60">
            Conversation
          </p>
          <p className="text-lg font-semibold">{title}</p>
        </div>
        {renderMessages()}
        {renderInput()}
      </div>
    </div>
  );

  const messageCenterShell = (
    <section
      className={cn(
        "rounded-3xl border border-border bg-card p-6 text-card-foreground shadow-xl",
        "dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900/80 dark:via-slate-900 dark:to-black dark:text-white dark:shadow-2xl",
        className
      )}>
      <header className="mb-6">
        <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground dark:text-white/60">
          {t("chat.panelBadge")}
        </p>
        <h1 className="text-2xl font-semibold">{t("chat.panelTitle")}</h1>
      </header>
      {conversationLayout}
    </section>
  );

  if (isPanel) {
    return (
      <>
        {messageCenterShell}
        {chatOverlays}
      </>
    );
  }

  if (!showLauncher || unreadTotal === 0) {
    return null;
  }

  return (
    <>
      <Button
        id="chat-widget-button"
        className="fixed bottom-6 right-6 z-40 shadow-lg"
        onClick={() => setIsOpen(true)}>
        {isClient ? t("chat.widgetClientLabel") : t("chat.widgetManagerLabel")}
        {unreadTotal > 0 && (
          <span className="ml-2 rounded-full bg-white/90 px-2 text-xs font-semibold text-black">
            {unreadTotal}
          </span>
        )}
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[min(92vh,960px)] overflow-y-auto border-none bg-transparent p-0 shadow-none sm:max-w-[min(96vw,1200px)]">
          {messageCenterShell}
        </DialogContent>
      </Dialog>
      {chatOverlays}
    </>
  );
};

export default ChatWidget;
